import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from '../../i18n/useTranslation';
import { Button } from '../atoms/Button/Button';
import { Card } from '../atoms/Card/Card';
import { Inline } from '../atoms/Inline/Inline';
import { Input } from '../atoms/Input/Input';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { Select } from '../atoms/Select/Select';
import { Stack } from '../atoms/Stack/Stack';
import { Text } from '../atoms/Typography/Text';
import { Textarea } from '../atoms/Textarea/Textarea';
import { Section } from '../templates/Section/Section';
import { TableWrap } from '../budget/BudgetPageParts';
import type { ControllerSource, SensorMapping } from '../../api/controller';
import {
  PUSH_INTERVALS,
  buildHomeAssistantConfig,
  isEntityId,
  parseEntityList,
  powerEntityOfEnergyKey,
} from './homeAssistantConfig';
import { controllerIngestUrl } from './ControllerSources';
import './Controller.css';

const COPIED_FEEDBACK_MS = 2500;
const EXAMPLE_PROBES = 'sensor.profilux_temperatur\nsensor.profilux_leitwert\nsensor.profilux_redox';

/** Probe entities already reported: everything but energy meters (they come from the power entity) and ignored keys. */
function reportedProbes(mappings: readonly SensorMapping[]): string[] {
  return mappings
    .filter((m) => m.kind !== 'energy' && m.kind !== 'power' && m.kind !== 'ignore' && isEntityId(m.sourceKey))
    .filter((m) => !m.sourceKey.startsWith('sensor.reeftracker_'))
    .map((m) => m.sourceKey);
}

export interface HomeAssistantSetupProps {
  sources: readonly ControllerSource[];
  mappings: readonly SensorMapping[];
}

/** Generates the Home Assistant package (window statistics, energy meter, push automation) for a source. */
export function HomeAssistantSetup({ sources, mappings }: HomeAssistantSetupProps) {
  const { t } = useTranslation();
  const id = useId();
  const active = sources.filter((s) => s.status === 'active');
  const [sourceId, setSourceId] = useState(active[0]?.id ?? '');
  const [probesText, setProbesText] = useState(() => reportedProbes(mappings).join('\n') || EXAMPLE_PROBES);
  // The package sends the power as its energy meter, so the power entity is read back from that key.
  const [powerEntity, setPowerEntity] = useState(
    () =>
      mappings.map((m) => powerEntityOfEnergyKey(m.sourceKey)).find((e): e is string => e !== null) ??
      mappings.find((m) => m.kind === 'power' && isEntityId(m.sourceKey))?.sourceKey ??
      '',
  );
  const [intervalHours, setIntervalHours] = useState(1);
  const [origin, setOrigin] = useState(() => mappings.find((m) => m.origin)?.origin ?? '');
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<number | null>(null);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  // Keep a valid selection when sources change (revoked / added).
  const selected = active.find((s) => s.id === sourceId) ?? active[0];

  const probes = parseEntityList(probesText);
  const power = powerEntity.trim();
  const powerInvalid = power !== '' && !isEntityId(power);

  const config = selected
    ? buildHomeAssistantConfig({
        endpointUrl: controllerIngestUrl(selected.id),
        probeEntities: probes.valid,
        powerEntity: power !== '' && !powerInvalid ? power : null,
        intervalHours,
        origin,
      })
    : '';

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(config);
      setCopyState('copied');
    } catch {
      setCopyState('failed');
    }
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopyState('idle'), COPIED_FEEDBACK_MS);
  };

  return (
    <Section title={t('controller.setup.title')} description={t('controller.setup.description')}>
      <Card>
        {!selected ? (
          <Text color="muted">{t('controller.setup.noActiveSource')}</Text>
        ) : (
          <Stack gap="4">
            {active.length > 1 && (
              <Select
                id={`${id}-source`}
                label={t('controller.setup.source')}
                value={selected.id}
                options={active.map((s) => ({ value: s.id, label: s.name }))}
                onChange={(e) => setSourceId(e.target.value)}
              />
            )}
            <div className="controller-setup__row">
              <Select
                id={`${id}-interval`}
                label={t('controller.setup.interval')}
                hint={t('controller.setup.intervalHint')}
                value={String(intervalHours)}
                options={PUSH_INTERVALS.map((h) => ({ value: String(h), label: t('controller.setup.intervalOption', { count: h }) }))}
                onChange={(e) => setIntervalHours(Number(e.target.value))}
              />
              <Input
                id={`${id}-origin`}
                label={t('controller.setup.origin')}
                helperText={t('controller.setup.originHint')}
                placeholder="GHL ProfiLux"
                value={origin}
                maxLength={100}
                onChange={(e) => setOrigin(e.target.value)}
              />
            </div>
            <Textarea
              id={`${id}-probes`}
              label={t('controller.setup.probes')}
              helperText={t('controller.setup.probesHint')}
              value={probesText}
              rows={5}
              onChange={(e) => setProbesText(e.target.value)}
              fullWidth
              error={probes.invalid.length > 0 ? t('controller.setup.invalidEntities', { entities: probes.invalid.join(', ') }) : undefined}
            />
            <Input
              id={`${id}-power`}
              label={t('controller.setup.power')}
              helperText={t('controller.setup.powerHint')}
              value={powerEntity}
              onChange={(e) => setPowerEntity(e.target.value)}
              error={powerInvalid ? t('controller.setup.invalidEntities', { entities: power }) : undefined}
              fullWidth
            />
            <Text>{t('controller.setup.steps')}</Text>
            <TableWrap label={t('controller.setup.codeLabel')} className="controller-setup__code">
              <pre className="controller-setup__pre">
                <code>{config}</code>
              </pre>
            </TableWrap>
            <Inline gap="3" align="center">
              <Button variant="primary" onClick={() => void copy()}>
                {t('controller.setup.copy')}
              </Button>
              <LiveRegion as="span">
                {copyState === 'copied' && <Text as="span" color="muted">{t('controller.setup.copied')}</Text>}
                {copyState === 'failed' && <Text as="span" color="muted">{t('controller.setup.copyFailed')}</Text>}
              </LiveRegion>
            </Inline>
          </Stack>
        )}
      </Card>
    </Section>
  );
}
